import type {
  ApiError,
  OrderStatus,
  OrderSummary,
  OrdersLookupResponse,
} from '../../shared/orderStatus';

export class ApiCallError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiCallError';
    this.status = status;
  }
}

async function parseJsonOrThrow<T>(res: Response): Promise<T> {
  const text = await res.text();
  let body: unknown;
  try {
    body = text ? JSON.parse(text) : {};
  } catch {
    throw new ApiCallError(res.status, 'Unexpected response from server.');
  }

  if (!res.ok || (body as ApiError | null)?.error === true) {
    const message = (body as ApiError | null)?.message ?? `Request failed (${res.status}).`;
    throw new ApiCallError(res.status, message);
  }
  return body as T;
}

export async function fetchOrderStatus(systemId: string): Promise<OrderStatus> {
  const res = await fetch(`/api/order-status/${encodeURIComponent(systemId)}`);
  const data = await parseJsonOrThrow<OrderStatus>(res);
  if (!Array.isArray(data.lines)) {
    throw new ApiCallError(502, 'Order status response was malformed.');
  }
  return data;
}

export async function lookupOrders(input: {
  email?: string;
  orderNumber?: string;
}): Promise<OrderSummary[]> {
  const res = await fetch('/api/orders-lookup', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const data = await parseJsonOrThrow<OrdersLookupResponse>(res);
  return data.orders;
}
